import Box from '@amzn/awsui-components-core/box';
import Icon from '@amzn/awsui-components-core/icon';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { faStar as regularStar } from '@fortawesome/free-regular-svg-icons';
import { faStar as solidStar, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreateOrUpdateApplicationFavoritesMutation } from 'src/API';
import { fetchGraphQLRequest } from 'src/api/graphql-api';
import { appSyncOperationName } from 'src/constants/AppConstants';
import * as mutations from 'src/graphql/mutations';
import { logger } from 'src/logger';
import { sanitizeHtml } from 'src/utilities/SanitizeHtml';
import { useAppContext } from '../context/AppContextProvider';
import { useAuth } from '../context/AuthContextProvider';
import { UserActionsEventTypes } from '../das-finsuite/das-finsuite-constants';
import { useNavigationItems } from '../side-navigation-v2/useNavigationItems';
import { PROFILE_DROPDOWN_ITEMS } from './ProfileDropdownConstants';
import './GlobalSearchBar.scss';
import {
  GroupedSearchResults,
  MAX_RESULTS_PER_CATEGORY,
  performSearch,
  QuickLinkItem,
  SearchCategory,
  SEARCH_CATEGORIES,
  SearchResultItem
} from './GlobalSearchUtils';

const buildQuickLinks = (navItems: ReturnType<typeof useNavigationItems>): QuickLinkItem[] => {
  const links: QuickLinkItem[] = [];
  let currentSection = '';

  // Side navigation links
  navItems.forEach((item) => {
    if (item.type === 'section-header') {
      currentSection = item.text;
    } else if (item.type === 'link' && item.href) {
      links.push({ id: `nav-${item.href}`, text: item.text, href: item.href, external: item.external, section: currentSection || 'Navigation' });
    }
  });

  // Profile dropdown links
  PROFILE_DROPDOWN_ITEMS.forEach((group) => {
    if ('items' in group && group.items) {
      group.items.forEach((item) => {
        if ('href' in item && item.href && item.text) {
          links.push({ id: `profile-${item.id}`, text: item.text, href: item.href, external: true, section: 'Quick Links' });
        }
      });
    } else if ('href' in group && group.href && group.text) {
      links.push({ id: `profile-${group.id}`, text: group.text, href: group.href, external: true, section: 'Quick Links' });
    }
  });

  return links;
};

export const GlobalSearchBar: React.FC = () => {
  const navigate = useNavigate();
  const { Alias: userAlias } = useAuth();
  const { parsedApplications, favoriteApplications, setFavoriteApplications, announcementsFlatEntity, homePageContent } = useAppContext();
  const navItems = useNavigationItems();

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<SearchCategory>('all');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeParsedApps = useMemo(() => parsedApplications.filter((app) => app.isActive), [parsedApplications]);
  const quickLinks = useMemo(() => buildQuickLinks(navItems), [navItems]);

  const welcomeContent = useMemo(() => homePageContent?.welcomeContentDetails || [], [homePageContent]);

  const financeCalendarEvents = useMemo(() => homePageContent?.financeCalendarEvents || [], [homePageContent?.financeCalendarEvents]);

  const results: GroupedSearchResults = useMemo(
    () => performSearch(query, activeParsedApps, announcementsFlatEntity, favoriteApplications, quickLinks, welcomeContent, financeCalendarEvents),
    [query, activeParsedApps, announcementsFlatEntity, favoriteApplications, quickLinks, welcomeContent, financeCalendarEvents]
  );

  const visibleResults = useMemo((): SearchResultItem[] => {
    if (activeCategory === 'all') {
      return [
        ...results.applications.slice(0, MAX_RESULTS_PER_CATEGORY),
        ...results.announcements.slice(0, MAX_RESULTS_PER_CATEGORY),
        ...results.pages.slice(0, MAX_RESULTS_PER_CATEGORY)
      ];
    }
    return (results[activeCategory] || []).slice(0, MAX_RESULTS_PER_CATEGORY * 2);
  }, [results, activeCategory]);

  const hasResults = visibleResults.length > 0;
  const showDropdown = isOpen && query.trim().length > 0;

  // Global keyboard shortcut: Option+S
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && (e.key.toLowerCase() === 's' || e.key === 'ß' || e.code === 'KeyS')) {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleResultClick = useCallback(
    (item: SearchResultItem) => {
      setIsOpen(false);
      setQuery('');

      if (item.category === 'announcements') {
        navigate('/announcements', { state: { filterByTitle: item.title } });
        return;
      }

      // Finance calendar items → navigate to home, switch tab, highlight
      if (item.id.startsWith('calendar-')) {
        navigate('/', { state: { highlightCalendarEvent: { scenario: item.meta, event: item.title } } });
        return;
      }

      // External links → new tab, internal links → navigate
      if (item.external) {
        if (item.href) window.open(item.href, '_blank', 'noopener,noreferrer');
        return;
      }

      if (item.href?.startsWith('/')) {
        navigate(item.href);
      } else if (item.href) {
        window.open(item.href, '_blank', 'noopener,noreferrer');
      }
    },
    [navigate]
  );

  const handleExternalLink = useCallback((e: React.MouseEvent, href: string) => {
    e.stopPropagation();
    window.open(href, '_blank', 'noopener,noreferrer');
  }, []);

  const toggleFavorite = useCallback(
    async (e: React.MouseEvent, item: SearchResultItem) => {
      e.stopPropagation();
      const isCurrentlyFavorite = favoriteApplications.includes(item.id);
      const updatedFavorites = isCurrentlyFavorite ? favoriteApplications.filter((id) => id !== item.id) : [...favoriteApplications, item.id];

      logger.info(`DaS FinSuite -> User Action: ${isCurrentlyFavorite ? 'Unfavorite' : 'Favorite'} App from Search`, {
        event_type: isCurrentlyFavorite ? UserActionsEventTypes.TOP_NAV_UNFAVORITE_APP : UserActionsEventTypes.TOP_NAV_FAVORITE_APP,
        value: item.title
      });

      setFavoriteApplications(updatedFavorites);

      try {
        await fetchGraphQLRequest<CreateOrUpdateApplicationFavoritesMutation>({
          query: mutations.createOrUpdateApplicationFavorites,
          variables: { input: { userAlias, applicationId: updatedFavorites } },
          requestType: appSyncOperationName.MUTATION,
          requestName: 'createOrUpdateApplicationFavorites'
        });
      } catch (error: any) {
        logger.error('Error updating favorites from search', error);
        setFavoriteApplications(favoriteApplications);
      }
    },
    [favoriteApplications, setFavoriteApplications, userAlias]
  );

  const handleShowMore = useCallback(
    (category: SearchCategory) => {
      setIsOpen(false);
      setQuery('');
      if (category === 'applications') navigate('/all-apps');
      else if (category === 'announcements') navigate('/announcements');
    },
    [navigate]
  );

  const renderCategoryResults = (category: SearchCategory, items: SearchResultItem[], label: string) => {
    if (items.length === 0) return null;
    const displayItems = items.slice(0, MAX_RESULTS_PER_CATEGORY);
    const hasMore = items.length > MAX_RESULTS_PER_CATEGORY;

    return (
      <div className="global-search__category" key={category}>
        <div className="global-search__category-header">
          <span className="global-search__category-title">{label}</span>
          {hasMore && category !== 'pages' && (
            <button className="global-search__show-more" onClick={() => handleShowMore(category)}>
              Show more
            </button>
          )}
        </div>
        {displayItems.map((item) => (
          <div key={`${item.category}-${item.id}`} className="global-search__result-item" onClick={() => handleResultClick(item)}>
            <div className="global-search__result-content">
              <div className="global-search__result-title">
                <span>{item.title}</span>
              </div>
              {item.description && (
                <div className="global-search__result-description" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.description) }} />
              )}
              {item.meta && <div className="global-search__result-meta">{item.meta}</div>}
            </div>
            {item.category === 'applications' && (
              <button
                className="global-search__favorite-btn"
                onClick={(e) => toggleFavorite(e, item)}
                aria-label={item.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <FontAwesomeIcon icon={item.isFavorite ? solidStar : regularStar} className={item.isFavorite ? 'global-search__star--active' : ''} />
              </button>
            )}
            {item.category === 'announcements' && item.href && (
              <button className="global-search__external-link-btn" onClick={(e) => handleExternalLink(e, item.href!)} aria-label="Open external link">
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </button>
            )}
            {item.category === 'pages' && item.external && item.href && (
              <button className="global-search__external-link-btn" onClick={(e) => handleExternalLink(e, item.href!)} aria-label="Open external link">
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="global-search" ref={containerRef}>
      <div className="global-search__input-wrapper">
        <Icon name="search" className="global-search__search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="global-search__input"
          placeholder="Search apps, links, and product launches..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setActiveCategory('all');
          }}
          onFocus={() => {
            if (query.trim()) setIsOpen(true);
          }}
          aria-label="Global search"
          aria-expanded={showDropdown}
          role="combobox"
          autoComplete="off"
        />
        {!query && <span className="global-search__shortcut-hint">⌥S</span>}
        {query && (
          <button
            className="global-search__clear-btn"
            onClick={() => {
              setQuery('');
              setIsOpen(false);
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            <Icon name="close" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="global-search__dropdown">
          <div className="global-search__sidebar">
            {SEARCH_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className={`global-search__sidebar-item ${activeCategory === cat.id ? 'global-search__sidebar-item--active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>
          <div className="global-search__results">
            {!hasResults && (
              <Box padding="l" textAlign="center">
                <span className="global-search__no-results">No results found for &quot;{query}&quot;</span>
              </Box>
            )}
            {activeCategory === 'all' ? (
              <>
                {renderCategoryResults('applications', results.applications, 'Applications')}
                {renderCategoryResults('pages', results.pages, 'Links')}
                {renderCategoryResults('announcements', results.announcements, 'Product Launches')}
              </>
            ) : (
              renderCategoryResults(
                activeCategory,
                results[activeCategory] || [],
                SEARCH_CATEGORIES.find((c) => c.id === activeCategory)?.label || ''
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};
